import { QueryClient } from '@tanstack/react-query';
import { ApiError } from '@/api/client';

/**
 * Server-state configuration.
 *
 * The defaults here encode two judgements:
 *
 * 1. **Never retry a 4xx.** A 403 will be a 403 on the fourth attempt too; retrying only delays
 *    the error the user needs to see, and on a mutation it risks repeating a side effect.
 * 2. **Short freshness, not zero.** Operational data changes constantly, so a 30-second stale time
 *    keeps navigation instant without showing yesterday's fleet state.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: true,
        retry: (failureCount, error) => {
          if (error instanceof ApiError && !error.isRetryable) return false;
          return failureCount < 2;
        },
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      },
      mutations: {
        // Mutations are never retried automatically. The backend is idempotent where it matters,
        // but a silent retry of "assign this asset" is not a decision a cache layer should make.
        retry: false,
      },
    },
  });
}
